import { type ReactNode, Suspense, useCallback, useState } from "react";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";

import ErrorBoundary from "components/error-boundary";
import { getScribeModalNestedDialogZIndex } from "features/medical-scribe/medical-scribe-constants";
import { lazyWithRetry } from "utils/lazy-with-retry.util";

import type { ScribeGuideDialogContext } from "./scribe-guide-dialog.component";
import type { ScribeGuideOpenedFrom } from "./scribe-onboarding-telemetry";

// Same layer as the guide dialog it stands in for, so it is not hidden by a scribe modal.
const NESTED_DIALOG_SX = {
  zIndex: (theme: Theme) =>
    getScribeModalNestedDialogZIndex(theme.zIndex.snackbar)
};

/**
 * Most people never open the dialog, so it stays out of the Scribes chunk. `reloadFallback:
 * false` for the same reason as the other lazy Scribe surfaces (ENG-1868): a recording's
 * `beforeunload` guard vetoes a recovery reload, and `React.lazy` memoises the failure.
 */
const ScribeGuideDialog = lazyWithRetry(
  () => import("./scribe-guide-dialog.component"),
  "scribe-guide-dialog",
  { reloadFallback: false }
);

/**
 * The dialog and the function that opens it. Mounted on the first open only, then kept, so its
 * close transition plays and a second open is instant.
 */
export default function useScribeGuideDialog(
  context: ScribeGuideDialogContext
): {
  openGuide: (openedFrom: ScribeGuideOpenedFrom) => void;
  guideDialog: ReactNode;
} {
  const [state, setState] = useState<{
    open: boolean;
    openedFrom: ScribeGuideOpenedFrom;
  } | null>(null);

  const openGuide = useCallback(
    (openedFrom: ScribeGuideOpenedFrom) => setState({ open: true, openedFrom }),
    []
  );
  const close = useCallback(
    () => setState((current) => current && { ...current, open: false }),
    []
  );

  const guideDialog = state ? (
    <ErrorBoundary
      FallbackComponent={
        <Dialog
          open={state.open}
          onClose={close}
          sx={context === "scribes_page" ? undefined : NESTED_DIALOG_SX}
        >
          <DialogContent>
            <Typography variant="body2">
              The guide couldn&rsquo;t be loaded. Reload the page to try again.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={close}>Close</Button>
          </DialogActions>
        </Dialog>
      }
    >
      <Suspense fallback={null}>
        <ScribeGuideDialog
          open={state.open}
          onClose={close}
          context={context}
          openedFrom={state.openedFrom}
        />
      </Suspense>
    </ErrorBoundary>
  ) : null;

  return { openGuide, guideDialog };
}
